import { RefObject, useEffect } from 'react';
import { format as dateFormat, differenceInWeeks, nextMonday } from 'date-fns';
import gettext from '@sdk/lang/gettext';
import { showTooltip as show, hideTooltip } from '@sdk/common/tooltip/Tooltip';
import useAuth from '@sdk/authentication/useAuth';
import { ShouldShowElement } from '@sdk/authentication/types/apiTypes';
import { SUBSCRIPTION_LEVELS } from 'sling-sdk/src/account/constants';

import { useShouldShow } from '@web/common/hooks/useShouldShow';

import { ConfirmationTooltip } from '../../components/ConfirmationTooltip';

import { Flag, TooltipContentWrapper } from './styles';

type TooltipConfig = {
  delayInMinutes: number;
  tooltipPlace: string;
  getTooltipContent: () => JSX.Element | string;
  showOnlyOnce?: boolean;
  tooltipWidth?: number;
};

export const useProjectedSalesTooltip = (
  flag: Partial<ShouldShowElement>,
  tooltipRef: RefObject<HTMLButtonElement>,
  dateFrom: Date,
  importedAt: Date,
  onTooltipHide: () => void,
  validators,
  subscriptionLevel: number
) => {
  const { currentUser } = useAuth();
  const isOutsideDateRange =
    differenceInWeeks(nextMonday(new Date(dateFrom)), nextMonday(new Date())) > 2;
  const isFreeCustomer = subscriptionLevel == SUBSCRIPTION_LEVELS.FREE;
  const tooltips: Partial<Record<ShouldShowElement, TooltipConfig>> = {
    projectedSalesImportInitialTooltip: {
      delayInMinutes: 60,
      tooltipPlace: 'left',
      getTooltipContent: () => {
        if (isFreeCustomer) {
          return (
            <TooltipContentWrapper>
              {gettext('This feature is available in Scheduling Pro. Click to learn more.')}
            </TooltipContentWrapper>
          );
        }
        if (isOutsideDateRange) {
          return (
            <TooltipContentWrapper>
              {gettext('Projections are only available\n 4 weeks in advance')}
            </TooltipContentWrapper>
          );
        }
        return (
          <TooltipContentWrapper>
            <Flag>Beta</Flag>
            <div>{gettext('Sync projected sales from Toast')}</div>
          </TooltipContentWrapper>
        );
      },
      showOnlyOnce: true,
    },

    projectedSalesImportOldDataTooltip: {
      delayInMinutes: 0,
      tooltipPlace: 'bottom',
      getTooltipContent: () => (
        <ConfirmationTooltip
          content={gettext(
            'Please re-sync for the most accurate projections. Last updated on %(date)s.',
            { date: dateFormat(importedAt, 'MMM do') }
          )}
          onClick={() => {
            hideTooltip(tooltipRef.current);
            onTooltipHide();
            dontShowAgain();
          }}
        />
      ),
      tooltipWidth: 250,
    },
  };

  const showTooltip = () => show(tooltipRef.current);
  const {
    delayInMinutes,
    tooltipPlace,
    getTooltipContent,
    showOnlyOnce = false,
    tooltipWidth = null,
  } = tooltips[flag];
  const { dontShowAgain, shouldShow } = useShouldShow(flag, currentUser, {
    delayInMinutes,
    validate: validators[flag],
  });

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    if (!shouldShow()) {
      return;
    }

    const timeoutId = setTimeout(() => {
      showTooltip();

      if (showOnlyOnce) {
        dontShowAgain();
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [currentUser, flag, shouldShow, dontShowAgain, showOnlyOnce, showTooltip]);

  return {
    getTooltipContent,
    showTooltip,
    tooltipPlace,
    tooltipRef,
    tooltipWidth,
    isDisabled: isOutsideDateRange,
  };
};
